// ABOUTME: Escalating recovery for a media element that has stopped delivering frames.
// ABOUTME: Pure controller — no React, no DOM. The player supplies the actions; this owns the ladder.
//
// WHY THIS EXISTS (measured 2026-08-17)
// `useVideoPlayer`'s whole FSM is driven by `timeupdate`: it is the only thing that detects a
// move reaching its hold, an exit reaching neutral, or a loop reaching its wrap. When a media
// element starves it stops firing `timeupdate` entirely — and `error` does NOT fire, because a
// buffer underrun is not an error. So a stall does not degrade the experience, it ENDS it: the
// picture freezes, `stateRef` stays PLAYING forever, and every subsequent command queues behind
// a boundary that will never arrive.
//
// That was reachable because `S001_PB_V004.mp4` is 359 MB / 31.0 Mbps and was being driven at
// playbackRate 5.0 — ~155 Mbps sustained and 150 fps of 1080p decode. The durable fix is the
// retimed master (see docs/reel-retime.md), which removes the demand. This guard is the belt:
// it makes the *symptom* unreachable whatever the cause — a slow network, a cold seek into a
// large file, a decode hiccup on a weaker machine in the room.
//
// The ladder is deliberately gentle before it is decisive:
//   1..maxBackOffs   ease the rate toward real time and re-issue play() — let the buffer win
//   after that       give up on this segment and land its composed frame, so the FSM unblocks
//
// Escalation re-arms itself rather than waiting for another `waiting` event, because a element
// that never leaves the waiting state never fires a second one.

export const STALL_GRACE_MS = 1200;      // a `waiting` shorter than this is a hiccup, not a stall
export const RATE_BACKOFF = 0.4;         // each rung multiplies the current rate by this
export const MIN_RECOVERY_RATE = 1;      // never back off below real time — that is not a fix
export const MAX_BACKOFF_ATTEMPTS = 2;   // rungs before we stop waiting and land the frame

/**
 * Next traversal rate on the way down. Floors at real time: backing off below 1× would make the
 * stall *worse* to look at (a crawl reads as broken) without reducing the bytes-per-second the
 * element needs, which is what actually starved.
 *
 * @param {number} current  the rate the element is running at now
 * @param {number} [min]    floor, defaults to real time
 * @returns {number}
 */
export function backOffRate(current, min = MIN_RECOVERY_RATE) {
  if (!Number.isFinite(current) || current <= 0) return min;
  return Math.max(min, current * RATE_BACKOFF);
}

/**
 * @param {{
 *   graceMs?: number,
 *   maxBackOffs?: number,
 *   onBackOff?: (attempt: number) => void,   // rung 1..maxBackOffs — try to keep playing
 *   onGiveUp?: (attempt: number) => void,    // ladder exhausted — unblock the FSM
 * }} [opts]
 */
export function createStallGuard({
  graceMs = STALL_GRACE_MS,
  maxBackOffs = MAX_BACKOFF_ATTEMPTS,
  onBackOff,
  onGiveUp,
} = {}) {
  let timer = null;
  let attempts = 0;

  const disarm = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const arm = () => {
    if (timer !== null) return; // already counting down — do not restart the clock
    timer = setTimeout(() => {
      timer = null;
      attempts += 1;
      if (attempts <= maxBackOffs) {
        onBackOff?.(attempts);
        arm(); // the element may still be starved; keep climbing without a fresh `waiting`
      } else {
        onGiveUp?.(attempts);
      }
    }, graceMs);
  };

  return {
    /** The element reported it has run out of data (`waiting`/`stalled`). */
    notifyWaiting: arm,

    /**
     * The element delivered something (`playing`, or a `timeupdate` that moved). Frames are
     * arriving, so this stall episode is over — reset the ladder so a later, unrelated stall
     * gets the full gentle treatment again rather than jumping straight to give-up.
     */
    notifyProgress() {
      disarm();
      attempts = 0;
    },

    /** New chapter / teardown — forget everything. */
    reset() {
      disarm();
      attempts = 0;
    },

    get attempts() { return attempts; },
    get armed() { return timer !== null; },
  };
}
